import { StrictMode } from 'react';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import * as TanStackQueryProvider from '../integrations/tanstack-query/root-provider';
import { routeTree } from '../routeTree.gen';
import ClerkProvider from '@/integrations/clerk/provider';
import { ConvexProvider } from '@/integrations/convex/convex-provider';
import { SignIn, useAuth } from '@clerk/clerk-react';
import { addRouterInstrumentation, Sentry } from '@/integrations/sentry';
import { useSentryUser } from '@/integrations/sentry/useSentryUser';

import '../styles.css';
import { LoadingPlaceholder } from '@components/loading-placeholder.tsx';
import { ThemeProvider } from '@repo/ui/components/theme-provider';

const TanStackQueryProviderContext = TanStackQueryProvider.getContext();
const router = createRouter({
  routeTree,
  context: {
    ...TanStackQueryProviderContext,
  },
  defaultPreload: 'intent',
  scrollRestoration: true,
  defaultStructuralSharing: true,
  defaultPreloadStaleTime: 0,
});

addRouterInstrumentation(router);

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const WebApp = () => {
  const { isSignedIn, isLoaded } = useAuth();
  useSentryUser();

  if (!isLoaded) {
    return <LoadingPlaceholder text="Checking you are logged in..." />;
  }

  if (!isSignedIn) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <SignIn />
      </div>
    );
  }

  return <RouterProvider router={router} />;
};

export function WebRoot() {
  return (
    <StrictMode>
      <Sentry.ErrorBoundary
        fallback={<p>Something went wrong. Please refresh the page.</p>}
        showDialog
      >
        <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
          <TanStackQueryProvider.Provider {...TanStackQueryProviderContext}>
            <ClerkProvider>
              <ConvexProvider>
                <WebApp />
              </ConvexProvider>
            </ClerkProvider>
          </TanStackQueryProvider.Provider>
        </ThemeProvider>
      </Sentry.ErrorBoundary>
    </StrictMode>
  );
}
